import * as THREE from 'three';
import { LENS_HEAT } from '@/ui/FloraLens';
import type { ObjectResolver } from './SelectionRenderer';

/**
 * Draws a site lens over the 3D design view: a neon glow on the plants that answer it, and a pool of
 * heat on the ground beneath them.
 *
 * ## Two channels, neither of which fights anything
 *
 * **Glow** is written to `material.emissive`. That channel is free: `FloraFactory.applySeasonalState`
 * writes only `material.color`, and no flora material sets an emissive at all. So the season keeps
 * full authority over hue — which it must, since the whole app is built on the date changing what a
 * plant looks like — and the lens keeps the glow, and a date scrub repaints one without disturbing
 * the other. Tinting `color` instead would have meant the lens and the calendar arguing over the same
 * number every frame.
 *
 * Both build paths give every plant its own materials — the procedural one makes them per view, and
 * the authored GLTF path clones them explicitly for this exact reason — so a tint can never leak from
 * one specimen to the next.
 *
 * **Pools** are one `InstancedMesh` of additive discs on the ground, sized to each canopy. They are
 * what makes the lens readable from a plan-ish camera, and additive blending is the point rather than
 * an implementation detail: where canopies overlap, the light sums, and a genuine hotspot appears
 * exactly where several good plants stand together. That is the reading a designer wants and the one
 * a per-plant glow cannot give.
 *
 * ## Why it can be asked to go away
 *
 * The pools live in the scene's overlay root, which the capture passes already hide. The glow does
 * not — it is on the plants themselves, and would otherwise walk into a photo-mode shot and into the
 * image an AI render is conditioned on. {@link setSuppressed} is what the application calls when it
 * clears the editor's chrome, and it strips the tint and puts it back afterwards.
 */

/** Neon pink, as a Three colour. See `LENS_HEAT` for why this colour and not another. */
const HEAT = new THREE.Color(LENS_HEAT.hot);
const UNRECORDED = new THREE.Color(LENS_HEAT.unrecorded);
const BLACK = new THREE.Color(0x000000);

/**
 * Glow at a reading of 1.
 *
 * Tuned down from the obvious "as bright as possible": at 0.85 an oak reading full marks turns into a
 * flat pink silhouette, and a designer can no longer see the plant they are being told about — the
 * canopy shape, the season, the shadow all go. The lens has to sit *on* the garden, not replace it,
 * because the whole point is to keep designing while it is on.
 */
const MAX_GLOW = 0.45;

/**
 * How far past the canopy radius a ground pool reaches.
 *
 * Comfortably wider than the plant, and that is the reading rather than a decoration: pools that stop
 * at the drip line never touch, and a heat map whose blobs never touch cannot show a hotspot. This is
 * what makes three good shrubs planted together look different from three planted apart.
 */
const POOL_SCALE = 1.7;

/** A floor on the pool radius, in metres, so a groundcover still casts something legible. */
const MIN_POOL_RADIUS = 0.9;

/** Lift off the ground, in metres, so a pool does not z-fight the terrain it lies on. */
const POOL_LIFT = 0.04;

/** Brightness of an unrecorded plant's pool — present, but plainly not a reading. */
const UNRECORDED_POOL = 0.18;

export interface LensTarget {
  /** Flora instance id, resolved to a scene object through the same resolver selection uses. */
  id: string;
  /** 0–1 reading. Ignored when `described` is false. */
  value: number;
  /** False where the record does not answer this lens — drawn neutral, never as a cold zero. */
  described: boolean;
  /** Canopy spread in metres, which sets the pool's size. */
  spreadMeters: number;
  position: { x: number; y: number; z: number };
}

/**
 * A soft radial disc, built once and shared by every pool.
 *
 * Alpha rather than colour, so the instance colour carries the reading and this carries only the
 * falloff — a hard-edged disc reads as a dinner plate rather than as heat.
 *
 * Returns null where there is no document to draw it on. A renderer is not entitled to assume a DOM
 * merely to exist: the pools then draw as flat quads, which nothing headless is looking at anyway,
 * and every other part of the lens still works.
 */
function createPoolTexture(): THREE.Texture | null {
  if (typeof document === 'undefined') return null;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (context) {
    const gradient = context.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.55, 'rgba(255,255,255,0.55)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

export class SiteLensRenderer {
  private readonly root = new THREE.Group();
  private readonly geometry: THREE.PlaneGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly texture: THREE.Texture | null;

  private pools: THREE.InstancedMesh | null = null;
  /** Ids currently carrying a glow, so clearing restores those and touches nothing else. */
  private readonly tinted = new Set<string>();
  /** The last thing asked for, so suppression can put it back without the caller re-supplying it. */
  private targets: readonly LensTarget[] = [];
  private suppressed = false;

  constructor(private readonly resolve: ObjectResolver) {
    this.root.name = 'lens-heat';
    // Drawn after the scene and never into the depth buffer: heat lies over the ground, and pools
    // that wrote depth would each punch a hole in the ones behind them.
    this.root.renderOrder = 2;

    this.geometry = new THREE.PlaneGeometry(1, 1);
    // Flat on the ground. A plane rather than a circle: the falloff texture is already round, and a
    // quad is four vertices against a circle's thirty-odd, times every plant on the site.
    this.geometry.rotateX(-Math.PI / 2);

    this.texture = createPoolTexture();
    this.material = new THREE.MeshBasicMaterial({
      ...(this.texture ? { map: this.texture } : {}),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
  }

  /** The pool group — the caller adds this to the scene overlay root. */
  get object(): THREE.Object3D {
    return this.root;
  }

  /**
   * Draw a lens. Passing an empty list is how a lens is switched off.
   *
   * Safe to call on every editor change, and it has to be: `FloraWorld.update` rebuilds a view when a
   * plant's maturity changes, and a rebuilt view arrives with fresh materials and no tint on them.
   */
  update(targets: readonly LensTarget[]): void {
    this.targets = targets;
    if (this.suppressed) return;
    this.applyGlow(targets);
    this.buildPools(targets);
  }

  /**
   * Hide the lens without forgetting it — for captures, photo mode, and anything else that wants the
   * garden rather than the reading. Restores exactly what was showing when it is switched back off.
   */
  setSuppressed(suppressed: boolean): void {
    if (this.suppressed === suppressed) return;
    this.suppressed = suppressed;
    if (suppressed) {
      this.clearGlow();
      this.root.visible = false;
    } else {
      this.root.visible = true;
      this.applyGlow(this.targets);
      this.buildPools(this.targets);
    }
  }

  clear(): void {
    this.targets = [];
    this.clearGlow();
    this.disposePools();
  }

  dispose(): void {
    this.clear();
    this.geometry.dispose();
    this.material.dispose();
    this.texture?.dispose();
    this.root.parent?.remove(this.root);
  }

  /* ----------------------------------- The glow ----------------------------------- */

  private applyGlow(targets: readonly LensTarget[]): void {
    const wanted = new Set<string>();

    for (const target of targets) {
      // An unrecorded plant gets no glow at all. Lighting it dimly would be a small claim about a
      // record that makes none, and the ground pool already says the plant is there and unread.
      if (!target.described || target.value <= 0) continue;
      const view = this.resolve(target.id);
      if (!view) continue;
      wanted.add(target.id);
      this.setEmissive(view, HEAT, target.value * MAX_GLOW);
    }

    for (const id of this.tinted) {
      if (wanted.has(id)) continue;
      const view = this.resolve(id);
      if (view) this.setEmissive(view, BLACK, 1);
    }

    this.tinted.clear();
    for (const id of wanted) this.tinted.add(id);
  }

  private clearGlow(): void {
    for (const id of this.tinted) {
      const view = this.resolve(id);
      if (view) this.setEmissive(view, BLACK, 1);
    }
    this.tinted.clear();
  }

  private setEmissive(view: THREE.Object3D, color: THREE.Color, intensity: number): void {
    view.traverse((node) => {
      const material = (node as Partial<THREE.Mesh>).material;
      if (Array.isArray(material)) {
        for (const entry of material) applyEmissive(entry, color, intensity);
      } else if (material) {
        applyEmissive(material, color, intensity);
      }
    });
  }

  /* ----------------------------------- The pools ---------------------------------- */

  private buildPools(targets: readonly LensTarget[]): void {
    const drawn = targets.filter((target) => !target.described || target.value > 0);

    if (drawn.length === 0) {
      this.disposePools();
      return;
    }

    // Reallocated only when the count changes: an instanced mesh cannot grow, and rebuilding one on
    // every date scrub would churn buffers for a garden whose plant count did not move.
    if (!this.pools || this.pools.count !== drawn.length) {
      this.disposePools();
      this.pools = new THREE.InstancedMesh(this.geometry, this.material, drawn.length);
      this.pools.frustumCulled = false;
      this.pools.name = 'lens-pools';
      this.root.add(this.pools);
    }

    const matrix = new THREE.Matrix4();
    const color = new THREE.Color();

    drawn.forEach((target, index) => {
      const radius = Math.max(MIN_POOL_RADIUS, (target.spreadMeters / 2) * POOL_SCALE);
      matrix.makeScale(radius * 2, 1, radius * 2);
      matrix.setPosition(target.position.x, target.position.y + POOL_LIFT, target.position.z);
      this.pools?.setMatrixAt(index, matrix);

      if (target.described) {
        // Scaled rather than faded: under additive blending, brightness *is* the reading, and it is
        // what lets two overlapping canopies sum into a hotspot instead of averaging into one.
        //
        // Kept well under full brightness because they *do* sum: a bed of six good plants stacks six
        // pools on the same patch of ground, and at full strength that patch goes flat white and the
        // designer loses the slope, the paving and the shadow under it.
        color.copy(HEAT).multiplyScalar(0.1 + target.value * 0.42);
      } else {
        color.copy(UNRECORDED).multiplyScalar(UNRECORDED_POOL);
      }
      this.pools?.setColorAt(index, color);
    });

    if (this.pools) {
      this.pools.instanceMatrix.needsUpdate = true;
      if (this.pools.instanceColor) this.pools.instanceColor.needsUpdate = true;
    }
  }

  private disposePools(): void {
    if (!this.pools) return;
    this.root.remove(this.pools);
    // The geometry and material are this renderer's own and shared across rebuilds, so only the
    // instance buffers go here; `dispose` releases the rest.
    this.pools.dispose();
    this.pools = null;
  }
}

/** Write the glow onto one material, if it is one of the kinds that has somewhere to put it. */
function applyEmissive(material: THREE.Material, color: THREE.Color, intensity: number): void {
  if (!(material instanceof THREE.MeshStandardMaterial)) return;
  material.emissive.copy(color);
  material.emissiveIntensity = intensity;
}
